/**
 * Gap Analysis Viewer Component
 * Displays content gaps with coverage metrics
 */

import React from 'react';
import type { GapItem } from '../../types/research-service';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react';

interface GapAnalysisViewerProps {
  gaps: GapItem[];
  title: string;
  loading?: boolean;
  error?: Error | null;
  onRefresh?: () => void;
}

export function GapAnalysisViewer({
  gaps,
  title,
  loading = false,
  error = null,
  onRefresh,
}: GapAnalysisViewerProps) {
  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle className="text-red-700">{title} - Error</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600">{error.message}</p>
          {onRefresh && (
            <Button
              onClick={onRefresh}
              variant="outline"
              size="sm"
              className="mt-4"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            {title}
          </CardTitle>
          <div className="flex items-center gap-2">
            {gaps.length > 0 && (
              <Badge variant="destructive">{gaps.length} gaps found</Badge>
            )}
            {onRefresh && (
              <Button
                onClick={onRefresh}
                variant="ghost"
                size="sm"
                disabled={loading}
              >
                <RefreshCw
                  className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
                />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            <span className="ml-3 text-gray-600">Analyzing gaps...</span>
          </div>
        ) : gaps.length === 0 ? (
          <div className="rounded-lg border border-green-200 bg-green-50 p-6 text-center">
            <p className="text-green-700 font-medium">
              No gaps found! Your content covers this area well.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {gaps.map((gap, idx) => (
              <GapItem key={idx} gap={gap} index={idx + 1} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Individual Gap Item Component
// ============================================================================

interface GapItemProps {
  gap: GapItem;
  index: number;
}

function GapItem({ gap, index }: GapItemProps) {
  const lexicalCoverage = gap.metadata.lexicalCoverage || 0;
  const semanticSimilarity = gap.metadata.semanticSimilarity || 0;

  // Color coding for coverage levels
  const getCoverageColor = (value: number): string => {
    if (value >= 0.7) return 'text-green-600 bg-green-100';
    if (value >= 0.4) return 'text-amber-600 bg-amber-100';
    return 'text-red-600 bg-red-100';
  };

  const getSeverityBadge = (): { text: string; variant: 'destructive' | 'default' | 'secondary' } => {
    const avgCoverage = (lexicalCoverage + semanticSimilarity) / 2;
    if (avgCoverage < 0.3) return { text: 'Critical', variant: 'destructive' };
    if (avgCoverage < 0.5) return { text: 'High', variant: 'default' };
    return { text: 'Medium', variant: 'secondary' };
  };

  const severity = getSeverityBadge();

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      {/* Header */}
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            Gap #{index}
          </Badge>
          <Badge variant={severity.variant} className="text-xs">
            {severity.text}
          </Badge>
        </div>
      </div>

      {/* Gap Text */}
      <p className="mb-3 text-sm font-medium text-gray-900">{gap.text}</p>

      {/* Reason */}
      <div className="mb-3 rounded bg-white p-3 text-xs text-gray-700">
        <span className="font-medium text-gray-900">Why this is a gap:</span>{' '}
        {gap.reason}
      </div>

      {/* Coverage Metrics */}
      <div className="mb-3 grid grid-cols-2 gap-3">
        <div className="rounded bg-white p-3">
          <div className="mb-1 text-xs text-gray-600">Lexical Coverage</div>
          <div className={`text-lg font-bold ${getCoverageColor(lexicalCoverage)}`}>
            {(lexicalCoverage * 100).toFixed(1)}%
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded bg-gray-200">
            <div
              className="h-full bg-gradient-to-r from-red-500 via-amber-500 to-green-500"
              style={{ width: `${lexicalCoverage * 100}%` }}
            />
          </div>
        </div>

        <div className="rounded bg-white p-3">
          <div className="mb-1 text-xs text-gray-600">Semantic Similarity</div>
          <div className={`text-lg font-bold ${getCoverageColor(semanticSimilarity)}`}>
            {(semanticSimilarity * 100).toFixed(1)}%
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded bg-gray-200">
            <div
              className="h-full bg-gradient-to-r from-red-500 via-amber-500 to-green-500"
              style={{ width: `${semanticSimilarity * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Keywords */}
      {gap.metadata.keywords && gap.metadata.keywords.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1">
          <span className="text-xs text-gray-600 mr-2">Keywords:</span>
          {gap.metadata.keywords.map((keyword, idx) => (
            <Badge key={idx} variant="outline" className="text-xs">
              {keyword}
            </Badge>
          ))}
        </div>
      )}

      {/* Source URL */}
      {gap.source_url && (
        <div className="pt-2 border-t border-amber-200">
          <a
            href={gap.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            View example from competitors
          </a>
        </div>
      )}
    </div>
  );
}

export default GapAnalysisViewer;
